(() => {
  if (window.radianPromotionInitialized) return;
  window.radianPromotionInitialized = true;

  function makeDecorative(node) {
    node.setAttribute('aria-hidden', 'true');
    node.inert = true;
    Array.from(node.attributes).forEach((attribute) => {
      if (attribute.name.startsWith('data-shopify-')) node.removeAttribute(attribute.name);
    });
    if (node.matches('a')) node.tabIndex = -1;
    return node;
  }

  function init(root) {
    if (root.dataset.promotionInitialized === 'true') return;
    const viewport = root.querySelector('[data-promotion-viewport]');
    const track = root.querySelector('[data-promotion-track]');
    const group = root.querySelector('[data-promotion-group]');
    if (!viewport || !track || !group || !group.children.length) return;

    const originals = Array.from(group.children);
    const rebuild = () => {
      group.replaceChildren(...originals);
      track.replaceChildren(group);

      // Measure one set once, then append every repeat in a single batch (no read per clone).
      const setWidth = group.scrollWidth;
      const repeats = setWidth > 0 ? Math.max(0, Math.ceil((viewport.clientWidth * 1.5) / setWidth) - 1) : 0;
      const clones = document.createDocumentFragment();
      for (let index = 0; index < Math.min(repeats * originals.length, 100); index += 1) {
        clones.appendChild(makeDecorative(originals[index % originals.length].cloneNode(true)));
      }
      group.appendChild(clones);

      const copy = group.cloneNode(true);
      copy.removeAttribute('data-promotion-group');
      makeDecorative(copy);
      copy.querySelectorAll('.promotion__item').forEach(makeDecorative);
      track.appendChild(copy);

      const secondsPer100px = Number(root.dataset.secondsPer100px) || 2;
      root.style.setProperty('--promotion-duration', `${(group.scrollWidth / 100) * secondsPer100px}s`);
      root.dataset.promotionReady = 'true';
    };

    root.dataset.promotionInitialized = 'true';

    // WCAG 2.2.2. Keyboard: focus pauses the scroll (CSS :focus-within); a bar with no links gets a
    // tab stop of its own for that. Touch: a tap that is not on a link toggles the pause.
    if (!group.querySelector('a[href], button')) viewport.tabIndex = 0;
    let touchStart = null;
    viewport.addEventListener('pointerdown', (event) => {
      touchStart = event.pointerType === 'touch' ? { x: event.clientX, y: event.clientY } : null;
    });
    viewport.addEventListener('pointerup', (event) => {
      if (!touchStart || event.pointerType !== 'touch') return;
      const moved = Math.abs(event.clientX - touchStart.x) > 10 || Math.abs(event.clientY - touchStart.y) > 10;
      touchStart = null;
      if (moved || event.target.closest('a[href], button')) return;
      root.dataset.promotionPaused = root.dataset.promotionPaused === 'true' ? 'false' : 'true';
    });

    // ResizeObserver reports the initial size itself; rebuild only when the width really changes.
    let lastWidth = -1;
    const observer = new ResizeObserver((entries) => {
      const width = Math.round(entries[entries.length - 1].contentRect.width);
      if (width === lastWidth) return;
      lastWidth = width;
      rebuild();
    });
    observer.observe(viewport);
    root.promotionObserver = observer;
    if (document.fonts && document.fonts.status !== 'loaded') document.fonts.ready.then(rebuild);
  }

  function initWithin(context) {
    context.querySelectorAll('[data-promotion]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  } else {
    initWithin(document);
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-promotion]').forEach((root) => root.promotionObserver?.disconnect());
  });

  // Theme editor: the moving copies carry no editor attributes and the selected message may sit
  // outside the bar, so a selected message stops the scroll and moves to the middle of the bar
  // (never so far that the space before the first message shows) until it is deselected.
  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target;
    const root = item.closest && item.closest('[data-promotion]');
    const viewport = root && root.querySelector('[data-promotion-viewport]');
    const track = root && root.querySelector('[data-promotion-track]');
    if (!viewport || !track || !track.contains(item)) return;
    root.dataset.promotionEditing = 'true';
    root.style.setProperty('--promotion-editor-shift', '0px');
    const itemBox = item.getBoundingClientRect();
    const viewportBox = viewport.getBoundingClientRect();
    const shift = (viewportBox.left + viewportBox.width / 2) - (itemBox.left + itemBox.width / 2);
    const rtl = getComputedStyle(viewport).direction === 'rtl';
    root.style.setProperty('--promotion-editor-shift', `${Math.round(rtl ? Math.max(0, shift) : Math.min(0, shift))}px`);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest && event.target.closest('[data-promotion]');
    if (!root || root.dataset.promotionEditing !== 'true') return;
    delete root.dataset.promotionEditing;
    root.style.removeProperty('--promotion-editor-shift');
  });
})();
